import { useState } from "react";
import { MessageSquare, Trash2 } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { uid } from "../lib/format";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { NativeSelect } from "./common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const AUTHOR = "hb-note-author";
const MAX = 280;
const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function NotesDialog({ kind, item, onClose }) {
  const { state, update } = useStore();
  const members = state.members || [];
  const [by, setBy] = useState(() => (members.some((m) => m.id === localStorage.getItem(AUTHOR)) ? localStorage.getItem(AUTHOR) : members[0]?.id));
  const [text, setText] = useState("");
  const names = Object.fromEntries(members.map((m) => [m.id, m.name]));
  const thread = [...(item.thread || [])].sort((a, b) => String(a.at).localeCompare(String(b.at)));
  const edit = (label, fn) => update(label, (d) => { const x = d[kind].find((i) => i.id === item.id); x.thread = fn(x.thread || []); });
  const add = () => {
    const t = text.trim().slice(0, MAX);
    if (!t) return;
    localStorage.setItem(AUTHOR, by);
    edit(`Note on ${item.name}`, (th) => [...th, { id: uid(), by, name: names[by] || "", text: t, at: new Date().toISOString() }]);
    setText("");
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="notes-dialog">
        <DialogHeader>
          <DialogTitle>Notes · {item.name}</DialogTitle>
          <DialogDescription>Short notes for your household. They travel with your data when you sync devices.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-2 max-h-[45vh] overflow-y-auto" data-testid="notes-list">
          {thread.length ? thread.map((n) => (
            <li key={n.id} className="rounded-xl bg-secondary px-3 py-2 text-sm group" data-testid={`note-${n.id}`}>
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span><b className="text-foreground" data-testid={`note-author-${n.id}`}>{names[n.by] || n.name || "Someone"}</b> · {when(n.at)}</span>
                <button onClick={() => edit("Delete note", (th) => th.filter((x) => x.id !== n.id))} aria-label="Delete note" className="hover:text-expense" data-testid={`note-delete-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <p className="mt-0.5 whitespace-pre-wrap break-words" data-testid={`note-text-${n.id}`}>{n.text}</p>
            </li>
          )) : <li className="text-sm text-muted-foreground" data-testid="notes-empty">No notes yet. Leave one for the household — e.g. "Paid early this month" or "Let's add $50 in March".</li>}
        </ul>
        <div className="space-y-2 pt-2 border-t">
          <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">From</span><NativeSelect value={by || ""} onChange={setBy} testid="note-author-select">{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</NativeSelect></div>
          <Textarea rows={2} value={text} maxLength={MAX} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.metaKey || e.ctrlKey) && add()} placeholder="Write a short note…" data-testid="note-input" />
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground" data-testid="note-chars">{text.length}/{MAX}</span>
            <Button size="sm" onClick={add} disabled={!text.trim()} data-testid="note-add-button">Add note</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function NotesButton({ kind, id, className = "" }) {
  const { state } = useStore();
  const [open, setOpen] = useState(false);
  const item = state[kind].find((x) => x.id === id);
  if (!item) return null;
  const n = item.thread?.length || 0;
  const last = n ? [...item.thread].sort((a, b) => String(b.at).localeCompare(String(a.at)))[0] : null;
  return (
    <>
      <button onClick={() => setOpen(true)} className={`inline-flex items-center gap-1 whitespace-nowrap text-xs rounded-full px-2 py-0.5 transition-colors ${n ? "bg-accent text-accent-foreground hover:bg-accent/70" : "text-muted-foreground hover:text-primary"} ${className}`} title={last ? `${last.name || ""}: ${last.text}` : "Add a household note"} data-testid={`notes-button-${id}`}>
        <MessageSquare className="h-3.5 w-3.5" /> <span data-testid={`notes-count-${id}`}>{n ? `${n} note${n === 1 ? "" : "s"}` : "Add note"}</span>
      </button>
      {open && <NotesDialog kind={kind} item={item} onClose={() => setOpen(false)} />}
    </>
  );
}
